import { useEffect, useRef } from "react";
import type { BrowseGroup, BrowseTrack } from "@touchscreen/api-client";
import { useBrowseGroupTracks } from "@touchscreen/hooks";
import { Tile } from "./Tile.js";
import { artworkUrl } from "../lib/artwork.js";
import { useOnScreen } from "../lib/useOnScreen.js";
import "./PlaylistRow.css";

export interface PlaylistRowProps {
  group: BrowseGroup;
  perPage: number;
  /** Omit when requests are disabled — tiles remain viewable without a Request CTA. */
  onSelectTrack?: (track: BrowseTrack) => void;
}

const SKELETON_COUNT = 6;

/**
 * One playlist: title + a horizontal scroller of its tracks. Tracks are only queried once this
 * row has actually scrolled on screen, and further pages of the same group load as the user
 * scrolls right — no per-playlist screen anymore, this row is the whole experience.
 */
export function PlaylistRow(props: PlaylistRowProps): React.JSX.Element {
  const [rootRef, onScreen] = useOnScreen<HTMLElement>();
  const { tracks, loading, loadingMore, hasMore, loadMore } = useBrowseGroupTracks(
    onScreen ? props.group.id : null,
    props.perPage,
  );

  const scrollerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Horizontal infinite scroll: root is the scroller itself, not the viewport, so this only
  // fires once the "load more" sentinel actually scrolls into the visible strip.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const sentinel = sentinelRef.current;
    if (!scroller || !sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) loadMore();
      },
      { root: scroller, rootMargin: "0px 200px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore, tracks.length]);

  return (
    <section className="playlist-row" ref={rootRef}>
      <h2 className="playlist-row__title">{props.group.title}</h2>
      <div className="playlist-row__scroller" ref={scrollerRef}>
        {!onScreen || (loading && tracks.length === 0)
          ? Array.from({ length: SKELETON_COUNT }, (_, i) => (
              <div key={i} className="playlist-row__item playlist-row__skeleton" />
            ))
          : tracks.map((track, index) => (
              // sm_id alone isn't a safe React key: the same track can appear more than once in a
              // playlist (or shift across pages as it loads), so qualify it with position.
              <div key={`${track.sm_id}-${index}`} className="playlist-row__item">
                <Tile
                  title={track.title}
                  subtitle={track.artist}
                  artworkUrl={track.artwork_ref ?? artworkUrl(track.sm_id)}
                  onClick={
                    props.onSelectTrack ? () => props.onSelectTrack?.(track) : undefined
                  }
                />
              </div>
            ))}
        {onScreen && hasMore ? (
          <div className="playlist-row__item playlist-row__sentinel" ref={sentinelRef}>
            {loadingMore ? <div className="playlist-row__skeleton" /> : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
